import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { Button } from '@/src/components/ui/button'
import { listJobs, getJobStats, stopJob, retryJob, deleteJob } from '@/src/api/job'
import { useState, useEffect } from 'react'
import { z } from 'zod'
import { DataTable, ColumnConfig } from '@/src/components/manage/tables/DataTable'
import { SimpleCopyCell } from '@/src/components/manage/tables/CopyableCell'
import { toast } from 'sonner'
import { Badge } from '@/src/components/ui/badge'
import { Pagination } from '@/src/components/manage/Pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/src/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/src/components/ui/dialog'
import { ConfirmModal } from '@/src/components/ui/confirm-modal'
import {
  ArrowsClockwiseIcon,
  StopCircleIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  FileTextIcon,
  ChartBarIcon,
  PlayCircleIcon,
  WarningCircleIcon,
  TrashIcon,
} from '@phosphor-icons/react'
import { useTranslation } from '@/src/i18n/client'
import { DateTimeText } from '@/src/components/ui/date-time'
import { FilterAction } from '@/src/components/manage/actions/FilterAction'

const jobStatusValues = ['all', 'CREATED', 'UPLOADED', 'PROCESSING', 'ANALYZING', 'DONE', 'FAILED'] as const

const searchSchema = z.object({
  status: z.enum(jobStatusValues).optional(),
  page: z.coerce.number().int().min(1).optional(),
})

export const Route = createFileRoute('/_manage/manage/admin/jobs/')({
  component: AdminJobsPage,
  validateSearch: searchSchema,
})

type JobStatus = 'CREATED' | 'UPLOADED' | 'PROCESSING' | 'ANALYZING' | 'DONE' | 'FAILED'

interface Job {
  id: string
  source: string
  status: JobStatus
  originalFilename: string
  contentType: string | null
  size: number | null
  rawKey: string
  processedKey: string | null
  externalId: string | null
  result: any
  error: string | null
  pollAttempt: number
  nextPollAt: Date | null
  analyzingStartedAt: Date | null
  lastPolledAt: Date | null
  lockToken: string | null
  lockExpiresAt: Date | null
  idempotencyKey: string | null
  createdAt: Date
  startedAt: Date | null
  completedAt: Date | null
  updatedAt: Date
  drawing: {
    id: string
    shareId: string
    name: string
    number: string
  } | null
}

const STATUS_COLORS: Record<JobStatus, string> = {
  CREATED: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  UPLOADED: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  PROCESSING: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  ANALYZING: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  DONE: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  FAILED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
}

function StatusBadge({ status, icon: Icon, onClick }: { status: JobStatus; icon?: React.ComponentType<{ className?: string }>; onClick?: () => void }) {
  return (
    <Badge className={`${STATUS_COLORS[status]} font-medium`} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      {Icon && <Icon className="inline w-4 h-4 mr-1" />}
      {status}
    </Badge>
  )
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  if (ms < 30000) return `${(ms / 1000).toFixed(1)}s`
  if (ms < 60000) return `${Math.round(ms / 1000)}s`
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`
  return `${Math.floor(ms / 3600000)}h ${Math.round((ms % 3600000) / 60000)}m`
}

function formatBytes(bytes: number | null): string {
  if (bytes === null) return '-'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// Separate component for smooth duration updates - only re-renders itself
function DurationCell({ startedAt, completedAt }: { startedAt: Date | null; completedAt: Date | null }) {
  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    // Only set up interval if job is running
    if (!startedAt || completedAt) return

    const interval = setInterval(() => {
      setNow(Date.now())
    }, 100) // Update every 100ms for smooth animation

    return () => clearInterval(interval)
  }, [startedAt, completedAt])

  if (startedAt && completedAt) {
    const duration = new Date(completedAt).getTime() - new Date(startedAt).getTime()
    return <>{formatDuration(duration)}</>
  }

  if (startedAt) {
    const duration = now - new Date(startedAt).getTime()
    return <>{formatDuration(duration)}...</>
  }

  return <>-</>
}

function StatCard({
  title,
  value,
  icon: Icon,
  color = 'text-muted-foreground'
}: {
  title: string
  value: string | number
  icon: React.ComponentType<{ className?: string }>
  color?: string
}) {
  return (
    <div className="bg-card rounded-lg border p-2 flex items-center gap-2 md:gap-3">
      <div className={`p-1 md:p-2 rounded-lg ${color}`}>
        <Icon className="w-4 h-4 md:w-6 md:h-6" />
      </div>
      <div>
        <div className="text-xl md:text-2xl font-semibold">{value}</div>
        <div className="text-xs md:text-sm text-muted-foreground">{title}</div>
      </div>
    </div>
  )
}

function AdminJobsPage() {
  const { t } = useTranslation('manage')
  const queryClient = useQueryClient()
  const searchParams = Route.useSearch()
  const navigate = Route.useNavigate()
  const statusFilter: JobStatus | 'all' = (searchParams.status ?? 'all') as JobStatus | 'all'
  const page = (searchParams.page ?? 1) - 1
  const setPage = (nextPage: number) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        page: nextPage > 0 ? nextPage + 1 : undefined,
      }),
    })
  }
  const setStatusFilter = (nextStatus: JobStatus | 'all') => {
    void navigate({
      search: (prev) => ({
        ...prev,
        status: nextStatus === 'all' ? undefined : nextStatus,
        page: undefined,
      }),
    })
  }
  const [selectedError, setSelectedError] = useState<{ job: Job; error: string } | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; jobId: string; filename: string }>({ open: false, jobId: '', filename: '' })
  const limit = 20

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['job', 'stats'],
    queryFn: () => getJobStats(),
    refetchInterval: (query) => {
      // Stop polling if page is hidden
      if (document.hidden) return false
      // Poll every 60s for stats
      return 60000
    },
    refetchIntervalInBackground: false,
  })

  // Fetch jobs
  const { data: jobsData, isLoading: jobsLoading, error: jobsError } = useQuery({
    queryKey: ['job', 'list', statusFilter, page],
    queryFn: () => listJobs({
      data: {
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit,
        offset: page * limit,
      }
    }),
    refetchInterval: (query) => {
      // Stop polling if page is hidden
      if (document.hidden) return false
      // Only poll if viewing in-progress jobs (CREATED, UPLOADED, PROCESSING, ANALYZING)
      if (statusFilter !== 'all' && statusFilter !== 'CREATED' && statusFilter !== 'UPLOADED' && statusFilter !== 'PROCESSING' && statusFilter !== 'ANALYZING') {
        return false // No need to poll completed/failed/stopped jobs
      }
      // Poll every 30s (reduced from 15s to minimize DB load)
      return 30000
    },
    refetchIntervalInBackground: false,
  })

  // Stop job mutation
  const stopMutation = useMutation({
    mutationFn: (id: string) => stopJob({ data: { id } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job'] })
      toast.success(t('jobs.jobStopped'))
    },
    onError: (err: any) => {
      toast.error(err?.message || t('jobs.failedToStopJob'))
    },
  })

  // Retry job mutation
  const retryMutation = useMutation({
    mutationFn: (id: string) => retryJob({ data: { id } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job'] })
      toast.success(t('jobs.jobRetried'))
    },
    onError: (err: any) => {
      toast.error(err?.message || t('jobs.failedToRetryJob'))
    },
  })

  // Delete job mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteJob({ data: { id } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job'] })
      toast.success(t('jobs.jobDeleted'))
      setDeleteConfirm({ open: false, jobId: '', filename: '' })
    },
    onError: (err: any) => {
      toast.error(err?.message || t('jobs.failedToDeleteJob'))
    },
  })

  const columns: ColumnConfig<Job>[] = [
    {
      key: 'id',
      label: 'ID',
      width: 'w-24',
      render: (job) => <SimpleCopyCell value={job.id} truncateLength={8} />,
    },
    {
      key: 'status',
      label: 'Status',
      width: 'w-28',
      align: 'center',
      render: (job) => {
        if (job.error) {
          return <StatusBadge status={job.status} icon={WarningCircleIcon} onClick={() => setSelectedError({ job, error: job.error! })} />
        }
        if (job.status === 'DONE' && job.drawing) {
          return (
            <Link
              to="/manage/admin/drawings/$id"
              params={{ id: job.drawing.id }}
              search={{ tab: 'data' }}
              className="inline-flex items-center gap-1 hover:opacity-80 transition-opacity"
              title={`View drawing: ${job.drawing.name || job.drawing.number || 'Drawing'}`}
            >
              <StatusBadge status={job.status} />
            </Link>
          )
        }
        return <StatusBadge status={job.status} />
      },
    },
    {
      key: 'originalFilename',
      label: 'File',
      render: (job) => (
        <div className="max-w-xs truncate" title={job.originalFilename}>
          {job.originalFilename}
        </div>
      ),
    },
    {
      key: 'source',
      label: 'Source',
      width: 'w-20',
      render: (job) => (
        <Badge variant="outline">{job.source}</Badge>
      ),
    },
    {
      key: 'size',
      label: 'Size',
      width: 'w-20',
      render: (job) => formatBytes(job.size),
    },
    {
      key: 'pollAttempt',
      label: 'Attempts',
      align: 'center',
      width: 'w-20',
      render: (job) => job.pollAttempt || 0,
    },
    {
      key: 'createdAt',
      label: 'Created',
      width: 'w-40',
      render: (job) => <DateTimeText>{String(job.createdAt)}</DateTimeText>,
    },
    {
      key: 'duration',
      label: 'Duration',
      align: 'right',
      width: 'w-24',
      render: (job) => <DurationCell startedAt={job.startedAt} completedAt={job.completedAt} />,
    },
    // {
    //   key: 'error',
    //   label: 'Error',
    //   width: 'w-16',
    //   render: (job) => job.error ? (
    //     <Button
    //       variant="ghost"
    //       size="icon"
    //       onClick={() => setSelectedError({ job, error: job.error! })}
    //       title="View error details"
    //       className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
    //     >
    //       <WarningCircleIcon className="w-5 h-5" weight="fill" />
    //     </Button>
    //   ) : null,
    // },
  ]

  const renderActions = (job: Job) => {
    const canStop = !['DONE', 'FAILED'].includes(job.status)
    const canRetry = job.status === 'FAILED'
    const canDelete = job.status === 'FAILED'

    return (
      <div className="flex gap-1">
        {canRetry && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => retryMutation.mutate(job.id)}
            disabled={retryMutation.isPending}
            title="Retry job"
          >
            <PlayCircleIcon className="w-4 h-4 text-green-600" />
          </Button>
        )}
        {canDelete && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setDeleteConfirm({ open: true, jobId: job.id, filename: job.originalFilename })}
            disabled={deleteMutation.isPending}
            title="Delete job"
          >
            <TrashIcon className="w-4 h-4 text-red-600" />
          </Button>
        )}
        {canStop && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => stopMutation.mutate(job.id)}
            disabled={stopMutation.isPending}
            title="Stop job"
          >
            <StopCircleIcon className="w-4 h-4 text-red-600" />
          </Button>
        )}
      </div>
    )
  }

  const jobs = jobsData?.jobs || []
  const total = jobsData?.total || 0
  const totalPages = Math.ceil(total / limit)

  return (
    <div className="bg-background text-foreground">
      <AdminToolbar>
        <AdminToolbar.Left>

          {/* Filter */}
          <div className="flex gap-4 items-center w-full">
            {/* Mobile: Select dropdown */}
            <Select 
              value={statusFilter} 
              onValueChange={(v) => { setStatusFilter(v as JobStatus | 'all'); }}
            >
              <SelectTrigger className="w-full md:hidden">
                <SelectValue>
                  {statusFilter === 'all' ? (
                    <span className="font-medium">All Statuses</span>
                  ) : (
                    <Badge className={`${STATUS_COLORS[statusFilter as JobStatus]} pointer-events-none`}>
                      {statusFilter}: {stats?.byStatus[statusFilter as JobStatus] || 0}
                    </Badge>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="font-medium">All Statuses</span>
                </SelectItem>
                {(['CREATED', 'UPLOADED', 'PROCESSING', 'ANALYZING', 'DONE', 'FAILED'] as JobStatus[]).map((status) => (
                  <SelectItem key={status} value={status}>
                    <div className="flex items-center gap-2">
                      <Badge className={`${STATUS_COLORS[status]} pointer-events-none`}>
                        {status}
                      </Badge>
                      <span className="text-muted-foreground">
                        {stats?.byStatus[status] || 0}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Desktop: Status badges */}
            <div className="hidden md:flex gap-2 flex-wrap">
              {(['CREATED', 'UPLOADED', 'PROCESSING', 'ANALYZING', 'DONE', 'FAILED'] as JobStatus[]).map((status) => (
                <Badge
                  key={status}
                  className={`${STATUS_COLORS[status]} cursor-pointer hover:opacity-80 transition-all ${
                    statusFilter === status 
                      ? 'ring-2 ring-offset-2 ring-primary scale-105' 
                      : 'opacity-60 hover:opacity-100'
                  }`}
                  onClick={() => setStatusFilter(status === statusFilter ? 'all' : status)}
                >
                  {status}: {stats?.byStatus[status] || 0}
                </Badge>
              ))}
            </div>
          </div>

        </AdminToolbar.Left>
        <AdminToolbar.Right>
          <Button variant="outline" size="lg" onClick={() => queryClient.invalidateQueries({ queryKey: ['job'] })}>
            <ArrowsClockwiseIcon className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            compact
          />
        </AdminToolbar.Right>
      </AdminToolbar>

      {/* Stats Section */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <StatCard
          title="Total Jobs"
          value={stats?.total || 0}
          icon={FileTextIcon}
        />
        <StatCard
          title="Processing"
          value={(stats?.byStatus.UPLOADED || 0) + (stats?.byStatus.PROCESSING || 0) + (stats?.byStatus.ANALYZING || 0)}
          icon={ClockIcon}
          color="text-yellow-600"
        />
        <StatCard
          title="Completed (24h)"
          value={stats?.recentCompletions || 0}
          icon={CheckCircleIcon}
          color="text-green-600"
        />
        <StatCard
          title="Failed"
          value={stats?.byStatus.FAILED || 0}
          icon={XCircleIcon}
          color="text-red-600"
        />
        <StatCard
          title="Avg. Time"
          value={stats?.avgProcessingTimeMs ? formatDuration(stats.avgProcessingTimeMs) : '-'}
          icon={ChartBarIcon}
          color="text-blue-600"
        />
        <StatCard
          title="Pending"
          value={(stats?.byStatus.CREATED || 0) + (stats?.byStatus.UPLOADED || 0)}
          icon={ArrowsClockwiseIcon}
          color="text-purple-600"
        />
      </div>

      {/* Jobs Table */}
      <DataTable<Job>
        data={jobs}
        columns={columns}
        isLoading={jobsLoading}
        error={jobsError instanceof Error ? jobsError : jobsError ? String(jobsError) : null}
        emptyMessage="No jobs found"
        actions={renderActions}
      />

      {/* Delete Confirmation */}
      <ConfirmModal
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ open, jobId: '', filename: '' })}
        title={t('jobs.deleteConfirmTitle')}
        description={
          <>
            {t('jobs.deleteConfirmDescription')}{' '}
            <strong className="break-all">{deleteConfirm.filename}</strong>
            <div className="mt-2 text-sm text-muted-foreground">
              {t('jobs.deleteConfirmWarning')}
            </div>
          </>
        }
        confirmLabel={t('jobs.deleteButton')}
        cancelLabel={t('cancel')}
        variant="destructive"
        onConfirm={() => deleteMutation.mutate(deleteConfirm.jobId)}
        isLoading={deleteMutation.isPending}
        loadingLabel={t('jobs.deletingButton')}
      />

      {/* Error Dialog */}
      <Dialog open={!!selectedError} onOpenChange={(open) => !open && setSelectedError(null)}>
        <DialogContent className="w-screen md:min-w-2xl max-w-full rounded-none sm:rounded-md max-h-[95vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <WarningCircleIcon className="w-5 h-5" weight="fill" />
              Job Error
            </DialogTitle>
            <DialogDescription>
              Error details for job {selectedError?.job.id.substring(0, 8)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 overflow-y-auto pr-2 flex-1">
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-1">Job Details</div>
              <div className="bg-muted rounded p-2 space-y-2 text-sm">
                <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
                  <span className="text-muted-foreground">ID:</span>
                  <span className="font-mono break-all">{selectedError?.job.id}</span>
                </div>
                <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
                  <span className="text-muted-foreground">File:</span>
                  <span className="break-all" title={selectedError?.job.originalFilename}>
                    {selectedError?.job.originalFilename}
                  </span>
                </div>
                <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
                  <span className="text-muted-foreground">Status:</span>
                  {selectedError?.job.status && <StatusBadge status={selectedError.job.status} />}
                </div>
                <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
                  <span className="text-muted-foreground">Failed At:</span>
                  <span className="break-all"><DateTimeText>{String(selectedError?.job.completedAt || selectedError?.job.updatedAt || '')}</DateTimeText></span>
                </div>
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-1">Error Message</div>
              <div className="bg-destructive/10 border border-destructive/20 rounded p-2 max-h-75 overflow-y-auto">
                <pre className="text-sm text-destructive whitespace-pre-wrap wrap-break-words font-mono">
                  {/* {JSON.stringify(selectedError?.error, null, 2)} */}
                  {selectedError?.error}
                </pre>
              </div>
            </div>
          </div>
          {selectedError?.job.status === 'FAILED' && (
            <div className="flex justify-end bg-background">
              <Button
                onClick={() => {
                  retryMutation.mutate(selectedError.job.id)
                  setSelectedError(null)
                }}
                disabled={retryMutation.isPending}
                className="flex items-center gap-2"
              >
                <PlayCircleIcon className="w-4 h-4" />
                Retry Job
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
